import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  ScrollView,
  Linking,
} from "react-native";
import { useFocusEffect } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import { useOrders, Order } from "../../hooks/useOrders";
import WebSidebar from "./WebSidebar";
import { API_URL } from "../../lib/config";
import { Ionicons } from "@expo/vector-icons";

type FilterType = "all" | "pending" | "confirmed";

const WebOrdersScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const isRTL = locale === "ar";
  const [filter, setFilter] = useState<FilterType>("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const {
    orders,
    isLoading,
    isRefreshing,
    fetchOrders,
    confirmOrder,
    cancelOrder,
    deleteOrder,
  } = useOrders();

  useFocusEffect(
    useCallback(() => {
      fetchOrders();
    }, [fetchOrders])
  );

  const counts = useMemo(
    () => ({
      all: orders.length,
      pending: orders.filter((o) => !o.confirmed).length,
      confirmed: orders.filter((o) => o.confirmed).length,
    }),
    [orders]
  );

  const filtered = useMemo(() => {
    if (filter === "pending") return orders.filter((o) => !o.confirmed);
    if (filter === "confirmed") return orders.filter((o) => o.confirmed);
    return orders;
  }, [orders, filter]);

  const getProductPhoto = (order: Order) => {
    if (!order.Product?.photo) return null;
    return order.Product.photo.startsWith("http")
      ? order.Product.photo
      : `${API_URL}${order.Product.photo}`;
  };

  const filters: { key: FilterType; label: string; color: string; bg: string }[] = [
    { key: "all", label: i18n.t("filterAll"), color: "#004E6D", bg: "#e0f2fe" },
    { key: "pending", label: i18n.t("filterPending"), color: "#d97706", bg: "#fffbeb" },
    { key: "confirmed", label: i18n.t("filterConfirmed"), color: "#059669", bg: "#ecfdf5" },
  ];

  return (
    <div
      key={`orders-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Text style={{ fontSize: 32 }}>📋</Text>
              </View>
              <Text style={styles.headerTitle}>{i18n.t("orders")}</Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Filter Bar */}
          <View style={styles.filterRow}>
            {filters.map((f) => {
              const isActive = filter === f.key;
              return (
                <TouchableOpacity
                  key={f.key}
                  onPress={() => setFilter(f.key)}
                  style={[
                    styles.filterChip,
                    {
                      backgroundColor: isActive ? f.bg : "#fff",
                      borderColor: isActive ? f.color : "#e5e7eb",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      { color: isActive ? f.color : "#6b7280" },
                    ]}
                  >
                    {f.label} ({counts[f.key]})
                  </Text>
                </TouchableOpacity>
              );
            })}

            {/* Refresh button */}
            <TouchableOpacity
              onPress={() => fetchOrders(true)}
              style={styles.refreshBtn}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <ActivityIndicator size="small" color="#004E6D" />
              ) : (
                <Ionicons name="refresh" size={18} color="#004E6D" />
              )}
            </TouchableOpacity>
          </View>

          {/* Content */}
          {isLoading && orders.length === 0 ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#004E6D" />
            </View>
          ) : filtered.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="receipt-outline" size={64} color="#d1d5db" />
              <Text style={styles.emptyTitle}>{i18n.t("noOrders")}</Text>
              <Text style={styles.emptyDesc}>{i18n.t("noOrdersDesc")}</Text>
            </View>
          ) : (
            <div className="flex flex-row gap-6 px-6 pb-6">
              {/* Orders Table */}
              <div className={`flex-1 ${selectedOrder ? "max-w-[60%]" : ""}`}>
                <View style={styles.tableCard}>
                  {/* Table Header */}
                  <View style={[styles.tableHeaderRow, isRTL && styles.rowRTL]}>
                    <Text style={[styles.thCell, { flex: 2 }]}>{i18n.t("clientName")}</Text>
                    <Text style={[styles.thCell, { flex: 2 }]}>{i18n.t("productname") || "Product"}</Text>
                    <Text style={[styles.thCell, { flex: 1 }]}>{i18n.t("quantity")}</Text>
                    <Text style={[styles.thCell, { flex: 1.5 }]}>{i18n.t("clientLocation")}</Text>
                    <Text style={[styles.thCell, { flex: 1 }]}>{i18n.t("status") || "Status"}</Text>
                    <Text style={[styles.thCell, { flex: 1.5 }]}>{i18n.t("actions") || "Actions"}</Text>
                  </View>

                  {/* Table Rows */}
                  {filtered.map((order) => {
                    const isSelected = selectedOrder?.id_order === order.id_order;
                    return (
                      <TouchableOpacity
                        key={order.id_order}
                        onPress={() => setSelectedOrder(isSelected ? null : order)}
                        activeOpacity={0.7}
                        style={[
                          styles.tableRow,
                          isRTL && styles.rowRTL,
                          isSelected && styles.tableRowSelected,
                        ]}
                      >
                        <View style={[styles.tdCell, { flex: 2, flexDirection: "row", alignItems: "center", gap: 8 }]}>
                          {getProductPhoto(order) ? (
                            <Image
                              source={{ uri: getProductPhoto(order)! }}
                              style={styles.tableThumb}
                              resizeMode="cover"
                            />
                          ) : (
                            <View style={[styles.tableThumb, styles.thumbPlaceholder]}>
                              <Ionicons name="cube-outline" size={16} color="#9ca3af" />
                            </View>
                          )}
                          <Text style={styles.tdText} numberOfLines={1}>
                            {order.client_fullname}
                          </Text>
                        </View>
                        <Text style={[styles.tdCell, styles.tdText, { flex: 2 }]} numberOfLines={1}>
                          {order.Product?.name || "-"}
                        </Text>
                        <Text style={[styles.tdCell, styles.tdText, { flex: 1 }]}>
                          ×{order.quantity}
                        </Text>
                        <Text style={[styles.tdCell, styles.tdText, { flex: 1.5 }]} numberOfLines={1}>
                          {order.location}
                        </Text>
                        <View style={[styles.tdCell, { flex: 1 }]}>
                          <View
                            style={[
                              styles.statusBadge,
                              order.confirmed ? styles.confirmedBadge : styles.pendingBadge,
                            ]}
                          >
                            <Text
                              style={[
                                styles.statusText,
                                order.confirmed ? styles.confirmedText : styles.pendingText,
                              ]}
                            >
                              {order.confirmed
                                ? i18n.t("orderConfirmed")
                                : i18n.t("orderPending")}
                            </Text>
                          </View>
                        </View>
                        <View style={[styles.tdCell, { flex: 1.5, flexDirection: "row", gap: 6 }]}>
                          {!order.confirmed ? (
                            <TouchableOpacity
                              style={styles.actionBtnConfirm}
                              onPress={(e) => {
                                e.stopPropagation?.();
                                confirmOrder(order.id_order);
                              }}
                            >
                              <Ionicons name="checkmark" size={16} color="#fff" />
                            </TouchableOpacity>
                          ) : (
                            <TouchableOpacity
                              style={styles.actionBtnCancel}
                              onPress={(e) => {
                                e.stopPropagation?.();
                                cancelOrder(order.id_order);
                              }}
                            >
                              <Ionicons name="close" size={16} color="#d97706" />
                            </TouchableOpacity>
                          )}
                          <TouchableOpacity
                            style={styles.actionBtnCall}
                            onPress={(e) => {
                              e.stopPropagation?.();
                              Linking.openURL(`tel:${order.client_phone}`);
                            }}
                          >
                            <Ionicons name="call-outline" size={16} color="#004E6D" />
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={styles.actionBtnDelete}
                            onPress={(e) => {
                              e.stopPropagation?.();
                              deleteOrder(order.id_order);
                            }}
                          >
                            <Ionicons name="trash-outline" size={16} color="#ef4444" />
                          </TouchableOpacity>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </div>

              {/* Detail Panel */}
              {selectedOrder && (
                <div className="w-[40%] min-w-[340px]">
                  <View style={styles.detailCard}>
                    {/* Detail Header */}
                    <View style={styles.detailHeader}>
                      <Text style={styles.detailTitle}>{i18n.t("orderDetails")}</Text>
                      <TouchableOpacity onPress={() => setSelectedOrder(null)}>
                        <Ionicons name="close" size={22} color="#6b7280" />
                      </TouchableOpacity>
                    </View>

                    {/* Status */}
                    <View
                      style={[
                        styles.detailStatusBadge,
                        selectedOrder.confirmed ? styles.confirmedBadge : styles.pendingBadge,
                      ]}
                    >
                      <Ionicons
                        name={selectedOrder.confirmed ? "checkmark-circle" : "time-outline"}
                        size={16}
                        color={selectedOrder.confirmed ? "#059669" : "#d97706"}
                      />
                      <Text
                        style={[
                          styles.detailStatusText,
                          selectedOrder.confirmed ? styles.confirmedText : styles.pendingText,
                        ]}
                      >
                        {selectedOrder.confirmed
                          ? i18n.t("orderConfirmed")
                          : i18n.t("orderPending")}
                      </Text>
                    </View>

                    {/* Product */}
                    {selectedOrder.Product && (
                      <View style={styles.detailProductSection}>
                        {getProductPhoto(selectedOrder) && (
                          <Image
                            source={{ uri: getProductPhoto(selectedOrder)! }}
                            style={styles.detailProductImage}
                            resizeMode="cover"
                          />
                        )}
                        <Text style={styles.detailProductName}>
                          {selectedOrder.Product.name}
                        </Text>
                        <View style={styles.detailPriceRow}>
                          <View style={styles.detailPriceItem}>
                            <Text style={styles.detailPriceLabel}>{i18n.t("unitPrice")}</Text>
                            <Text style={styles.detailPriceValue}>
                              {selectedOrder.Product.price_sell} DA
                            </Text>
                          </View>
                          <View style={styles.detailPriceDivider} />
                          <View style={styles.detailPriceItem}>
                            <Text style={styles.detailPriceLabel}>{i18n.t("quantity")}</Text>
                            <Text style={styles.detailPriceValue}>×{selectedOrder.quantity}</Text>
                          </View>
                          <View style={styles.detailPriceDivider} />
                          <View style={styles.detailPriceItem}>
                            <Text style={styles.detailPriceLabel}>{i18n.t("totalPrice")}</Text>
                            <Text style={[styles.detailPriceValue, { color: "#004E6D", fontWeight: "700" }]}>
                              {selectedOrder.Product.price_sell * selectedOrder.quantity} DA
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}

                    {/* Client Info */}
                    <View style={styles.detailInfoSection}>
                      <DetailRow
                        icon="person-outline"
                        label={i18n.t("clientName")}
                        value={selectedOrder.client_fullname}
                      />
                      <DetailRow
                        icon="call-outline"
                        label={i18n.t("clientPhone")}
                        value={selectedOrder.client_phone}
                        isPhone
                      />
                      <DetailRow
                        icon="location-outline"
                        label={i18n.t("clientLocation")}
                        value={selectedOrder.location}
                      />
                      <DetailRow
                        icon="calendar-outline"
                        label={i18n.t("orderDate")}
                        value={new Date(selectedOrder.createdAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      />
                    </View>

                    {/* Actions */}
                    <View style={styles.detailActions}>
                      {!selectedOrder.confirmed ? (
                        <TouchableOpacity
                          style={styles.detailConfirmBtn}
                          onPress={() => {
                            confirmOrder(selectedOrder.id_order);
                            setSelectedOrder({ ...selectedOrder, confirmed: true });
                          }}
                        >
                          <Ionicons name="checkmark" size={18} color="#fff" />
                          <Text style={styles.detailConfirmText}>{i18n.t("confirmOrder")}</Text>
                        </TouchableOpacity>
                      ) : (
                        <TouchableOpacity
                          style={styles.detailCancelBtn}
                          onPress={() => {
                            cancelOrder(selectedOrder.id_order);
                            setSelectedOrder({ ...selectedOrder, confirmed: false });
                          }}
                        >
                          <Ionicons name="close" size={18} color="#d97706" />
                          <Text style={styles.detailCancelText}>{i18n.t("cancelOrder")}</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        style={styles.detailDeleteBtn}
                        onPress={() => {
                          deleteOrder(selectedOrder.id_order);
                          setSelectedOrder(null);
                        }}
                      >
                        <Ionicons name="trash-outline" size={18} color="#ef4444" />
                      </TouchableOpacity>
                    </View>
                  </View>
                </div>
              )}
            </div>
          )}
        </ScrollView>
      </View>
    </div>
  );
};

const DetailRow = ({
  icon,
  label,
  value,
  isPhone,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string;
  isPhone?: boolean;
}) => (
  <View style={styles.detailRow}>
    <Ionicons name={icon} size={18} color="#004E6D" />
    <View style={{ flex: 1, marginLeft: 10 }}>
      <Text style={styles.detailLabel}>{label}</Text>
      {isPhone ? (
        <TouchableOpacity onPress={() => Linking.openURL(`tel:${value}`)}>
          <Text style={styles.detailValuePhone}>{value}</Text>
        </TouchableOpacity>
      ) : (
        <Text style={styles.detailValue}>{value}</Text>
      )}
    </View>
  </View>
);

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
    backgroundColor: "#F5F5F5",
  },
  scrollView: {
    flex: 1,
  },
  // Header
  headerSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
  },
  headerIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: "#e0f2fe",
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: "#1a1a2e",
  },
  headerBar: {
    height: 3,
    backgroundColor: "#0596D0",
    borderRadius: 2,
    marginTop: 12,
  },
  // Filters
  filterRow: {
    flexDirection: "row",
    paddingHorizontal: 24,
    paddingBottom: 16,
    gap: 10,
    alignItems: "center",
  },
  filterChip: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  filterChipText: {
    fontSize: 14,
    fontWeight: "600",
  },
  refreshBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginLeft: "auto",
  },
  // Loading / Empty
  loadingContainer: {
    paddingVertical: 80,
    alignItems: "center",
  },
  emptyContainer: {
    paddingVertical: 80,
    alignItems: "center",
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#374151",
    marginTop: 16,
  },
  emptyDesc: {
    fontSize: 14,
    color: "#9ca3af",
    marginTop: 8,
  },
  // Table
  tableCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f8fafc",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  thCell: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    alignItems: "center",
  },
  tableRowSelected: {
    backgroundColor: "#f0f9ff",
  },
  tdCell: {
    paddingRight: 8,
  },
  tdText: {
    fontSize: 14,
    color: "#374151",
  },
  tableThumb: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  thumbPlaceholder: {
    backgroundColor: "#f3f4f6",
    justifyContent: "center",
    alignItems: "center",
  },
  // Status badges
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: "flex-start",
  },
  confirmedBadge: {
    backgroundColor: "#ecfdf5",
  },
  pendingBadge: {
    backgroundColor: "#fffbeb",
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
  },
  confirmedText: {
    color: "#059669",
  },
  pendingText: {
    color: "#d97706",
  },
  rowRTL: {
    flexDirection: "row-reverse",
  },
  // Action buttons
  actionBtnConfirm: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#059669",
    justifyContent: "center",
    alignItems: "center",
  },
  actionBtnCancel: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#fffbeb",
    borderWidth: 1,
    borderColor: "#fde68a",
    justifyContent: "center",
    alignItems: "center",
  },
  actionBtnCall: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#f0f9ff",
    justifyContent: "center",
    alignItems: "center",
  },
  actionBtnDelete: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#fef2f2",
    justifyContent: "center",
    alignItems: "center",
  },
  // Detail panel
  detailCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  detailHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  detailTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#1f2937",
  },
  detailStatusBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    gap: 6,
    marginBottom: 20,
  },
  detailStatusText: {
    fontSize: 14,
    fontWeight: "600",
  },
  detailProductSection: {
    backgroundColor: "#f9fafb",
    borderRadius: 14,
    padding: 16,
    alignItems: "center",
    marginBottom: 20,
  },
  detailProductImage: {
    width: 80,
    height: 80,
    borderRadius: 12,
    marginBottom: 10,
  },
  detailProductName: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1f2937",
    textAlign: "center",
    marginBottom: 12,
  },
  detailPriceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  detailPriceItem: {
    alignItems: "center",
    paddingHorizontal: 12,
  },
  detailPriceLabel: {
    fontSize: 11,
    color: "#9ca3af",
    marginBottom: 2,
  },
  detailPriceValue: {
    fontSize: 15,
    fontWeight: "600",
    color: "#374151",
  },
  detailPriceDivider: {
    width: 1,
    height: 28,
    backgroundColor: "#e5e7eb",
  },
  detailInfoSection: {
    gap: 14,
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  detailLabel: {
    fontSize: 12,
    color: "#9ca3af",
  },
  detailValue: {
    fontSize: 14,
    color: "#374151",
    fontWeight: "500",
    marginTop: 1,
  },
  detailValuePhone: {
    fontSize: 14,
    color: "#004E6D",
    fontWeight: "500",
    textDecorationLine: "underline",
    marginTop: 1,
  },
  detailActions: {
    flexDirection: "row",
    gap: 10,
  },
  detailConfirmBtn: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#059669",
    paddingVertical: 12,
    borderRadius: 12,
    gap: 6,
  },
  detailConfirmText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
  detailCancelBtn: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fffbeb",
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#fde68a",
    gap: 6,
  },
  detailCancelText: {
    color: "#d97706",
    fontSize: 14,
    fontWeight: "600",
  },
  detailDeleteBtn: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: "#fef2f2",
    borderWidth: 1,
    borderColor: "#fecaca",
  },
});

export default WebOrdersScreen;
